import { IconButton, InputAdornment, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SearchIcon from '@material-ui/icons/Search';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useResourceBundles } from '../../../contexts/resource-bundles-context';
import { Select, OptionProps } from '../../common/select';
import { TextField } from '../../common/text-field';
import { HeatmapView } from './heatmap-tabs';

export type SortOption =
  | 'name'
  | 'assignedCount'
  | 'unassignedVentures'
  | 'overloadedMentors';

interface SearchFieldConfig {
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
}

interface HeatmapHeaderProps {
  activeView: HeatmapView;
  onViewChange: (view: HeatmapView) => void;
  primarySearch: SearchFieldConfig;
  secondarySearch: SearchFieldConfig;
  sortBy: SortOption;
  onSortChange: (value: SortOption) => void;
  onClearAll: () => void;
  showClearAll: boolean;
}

const DEBOUNCE_MS = 500;

const getViewOptions = (
  mentorLabel: string,
): OptionProps<HeatmapView>[] => [
  { label: `Venture → ${mentorLabel}`, value: 'venture-mentor' },
  { label: `${mentorLabel} → Venture`, value: 'mentor-venture' },
];

const BASE_SORT_OPTIONS: OptionProps<SortOption>[] = [
  { label: 'Sort: By Name', value: 'name' },
  { label: 'Sort: By # Assigned', value: 'assignedCount' },
];

const VENTURE_SORT_OPTIONS: OptionProps<SortOption>[] = [
  ...BASE_SORT_OPTIONS,
  { label: 'Show: Unassigned Ventures', value: 'unassignedVentures' },
];

const getMentorSortOptions = (
  mentorsLabel: string,
): OptionProps<SortOption>[] => [
    ...BASE_SORT_OPTIONS,
    {
      label: `Show: Overloaded ${mentorsLabel}`,
      value: 'overloadedMentors',
    },
  ];

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '0 0 24px',
    boxSizing: 'border-box',
  },
  filterList: {
    display: 'flex',
    alignItems: 'center',
    flex: 1,
    gap: 15,
  },
  filterItem: {},
  searchItem: {
    flex: 1,
    minWidth: 0,
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  viewSelect: {
    width: 190,

    '& > div': {
      minHeight: 38,
    },
  },
  searchInput: {
    width: '100%',
  },
  filterSelect: {
    width: 220,

    '& > div': {
      minHeight: 38,
    },
  },
  containerRemoveFilter: {},
  iconRemoveFilter: {
    width: 20,
  },
}));

function DebouncedSearch({
  placeholder,
  value,
  onChange,
  className,
}: SearchFieldConfig & { className: string }) {
  const [local, setLocal] = useState(value);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    setLocal(value);
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setLocal(v);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => onChange(v), DEBOUNCE_MS);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    setLocal('');
    if (timerRef.current) clearTimeout(timerRef.current);
    onChange('');
  }, [onChange]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <TextField
      placeholder={placeholder}
      value={local}
      onChange={handleChange}
      className={className}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchIcon />
          </InputAdornment>
        ),
        endAdornment: local ? (
          <InputAdornment position='end'>
            <IconButton
              size='small'
              onClick={handleClear}
              aria-label='Clear search'
            >
              <CloseIcon fontSize='small' />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
      small
      data-testid={`heatmap-search-${placeholder
        .replace(/\s+/g, '-')
        .toLowerCase()}`}
    />
  );
}

const HeatmapHeader = memo(function HeatmapHeader({
  activeView,
  onViewChange,
  primarySearch,
  secondarySearch,
  sortBy,
  onSortChange,
  onClearAll,
  showClearAll,
}: HeatmapHeaderProps) {
  const classes = useStyles();
  const { rb } = useResourceBundles();

  const viewOptions = useMemo(
    () => getViewOptions(rb('mentor-u')),
    [rb],
  );

  const sortOptions = useMemo(
    () =>
      activeView === 'venture-mentor'
        ? VENTURE_SORT_OPTIONS
        : getMentorSortOptions(rb('mentors-u')),
    [activeView, rb],
  );

  return (
    <div className={classes.root} data-testid='heatmap-header'>
      <div className={classes.filterList}>
        <div className={classes.filterItem}>
          <Select<HeatmapView>
            className={classes.viewSelect}
            value={activeView}
            options={viewOptions}
            onSelect={(item) => {
              if (item) onViewChange(item.value);
            }}
          />
        </div>

        <div className={classes.searchItem}>
          <DebouncedSearch {...primarySearch} className={classes.searchInput} />
        </div>

        <div className={classes.searchItem}>
          <DebouncedSearch
            {...secondarySearch}
            className={classes.searchInput}
          />
        </div>

        <div className={classes.filterItem}>
          <Select<SortOption>
            className={classes.filterSelect}
            value={sortBy}
            options={sortOptions}
            onSelect={(item) => {
              if (item) onSortChange(item.value);
            }}
          />
        </div>

        {showClearAll && (
          <Tooltip title='Clear all filters'>
            <IconButton
              data-testid='heatmap-clear-filters'
              className={classes.containerRemoveFilter}
              onClick={onClearAll}
            >
              <img
                src='/filter.svg'
                alt='Clear filters'
                className={classes.iconRemoveFilter}
              />
            </IconButton>
          </Tooltip>
        )}
      </div>
    </div>
  );
});

export default HeatmapHeader;
